import { WaterlilyClient, createQueryKeys } from '@/api/waterlily';

export const advisorAdminKeys = createQueryKeys('advisorAdmin');

export type AdvisorSearchParams = { advisorId: string } | { advisorEmail: string };

export type ClientSearchParams = { clientId: string };

export type ClientExportBundle = {
  version: number;
  exportedAt: string;
  exportedBy?: string;
  clientIds: string[];
  tables: Record<string, Record<string, unknown>[]>;
};

export type ClientImportResult = {
  imported: Record<string, { inserted: number; total: number }>;
  clientIdMap: Record<string, string>;
}
// Current session user's raw DB records. Rendered generically (key/value), so kept loosely typed.
export type SelfRecordResponse = {
  user: Record<string, unknown> | null;
  advisor: Record<string, unknown> | null;
};

export type ClientSearchResponse = {
  client: Client;
  advisor: {
    advisorId: string;
    advisorFirstName: string;
    advisorLastName: string;
    advisorEmail: string;
  };
  surveys: Survey[];
};

export type UpdateAdvisorFieldData = {
  advisorFieldKey: string;
  advisorFieldValue: string;
};

export type ManageReportingAttributeData = {
  attributeLabel: string;
  attributeValue?: string;
  action: 'create' | 'update' | 'delete';
};

export type AdvisorSearchResponse = {
  advisor: Advisor;
  clientCount: number;
};

export type DeleteAdvisorAccountResponse = {
  success: boolean;
  advisorId: string;
  deletedClientCount: number;
};

export type PreRegisteredFeatureFlag = {
  id: string;
  email: string;
  featureFlags: FeatureFlags;
  createdBy: string;
  consumedAt: string | null;
  advisorId: string | null;
  createdAt: string;
};

export type PreRegisteredFeatureFlagSearchResponse = {
  found: boolean;
  preRegisteredFeatureFlag: PreRegisteredFeatureFlag | null;
};

export type PreRegisteredFeatureFlagsBulkResponse = {
  inserted: number;
  skipped: number;
  rows: PreRegisteredFeatureFlag[];
};

export type PreRegisteredTeamRelationship = {
  id: string;
  supervisorEmail: string;
  subordinateEmail: string;
  enabledClientAccess: boolean;
  accessMode: ClientAccessMode;
  status: 'pending' | 'established';
  createdBy: string;
  createdAt: string;
  establishedAt: string | null;
  supervisorAdvisorId: string | null;
  subordinateAdvisorId: string | null;
};

export type PreRegisteredTeamRelationshipSearchResponse = {
  found: boolean;
  preRegisteredTeamRelationships: PreRegisteredTeamRelationship[];
};

export type PreRegisteredTeamRelationshipsBulkResponse = {
  inserted: number;
  skipped: number;
  rows: PreRegisteredTeamRelationship[];
};

export type CarescoutStateProbeParams = {
  quoteId: string;
  clientId: string;
  state: string;
};

export type CarescoutStateProbeResponse = {
  pdfBase64: string;
};

export const advisorAdminApi = {
  searchAdvisor: (params: AdvisorSearchParams) =>
    WaterlilyClient.post<AdvisorSearchResponse>('/admin-search/wizard-advisor', params),

  searchClient: (params: ClientSearchParams) =>
    WaterlilyClient.post<ClientSearchResponse>('/admin-search/wizard-client', params),

  exportClient: (params: { clientId: string; anonymizeNames: boolean }) =>
    WaterlilyClient.post<ClientExportBundle>('/wizard-client-export', params),

  importClient: (bundle: ClientExportBundle) =>
    WaterlilyClient.post<ClientImportResult>('/wizard-client-import', { bundle }),

  updateAdvisorField: (advisorId: string, data: UpdateAdvisorFieldData) =>
    WaterlilyClient.post<string>(`/wizard-advisor-field-update/${advisorId}`, {
      advisorId,
      ...data,
    }),

  updateAdvisorAccountIdentifier: (advisorId: string, data: UpdateAdvisorFieldData) =>
    WaterlilyClient.post<string>(`/wizard-advisor-account-identifier/${advisorId}`, {
      advisorId,
      ...data,
    }),

  manageReportingAttribute: (advisorId: string, data: ManageReportingAttributeData) =>
    WaterlilyClient.post<string>(`/wizard-advisor-reporting-attribute/${advisorId}`, {
      advisorId,
      ...data,
    }),

  deleteAdvisorAccount: (advisorId: string) =>
    WaterlilyClient.delete<DeleteAdvisorAccountResponse>(
      `/wizard-advisor-account/${advisorId}`,
    ),

  addPreRegisteredFeatureFlag: (data: { email: string; featureFlags: FeatureFlags }) =>
    WaterlilyClient.post<PreRegisteredFeatureFlag>('/wizard-pre-registered-feature-flag', data),

  addPreRegisteredFeatureFlagsBulk: (data: { emails: string[]; featureFlags: FeatureFlags }) =>
    WaterlilyClient.post<PreRegisteredFeatureFlagsBulkResponse>('/wizard-pre-registered-feature-flags-bulk', data),

  searchPreRegisteredFeatureFlag: (data: { email: string }) =>
    WaterlilyClient.post<PreRegisteredFeatureFlagSearchResponse>('/search/wizard-pre-registered-feature-flag', data),

  deletePreRegisteredFeatureFlag: (id: string) =>
    WaterlilyClient.delete<{ success: boolean }>(`/wizard-pre-registered-feature-flag/${id}`),

  fetchPreRegisteredFeatureFlags: () =>
    WaterlilyClient.get<PreRegisteredFeatureFlag[]>('/wizard-pre-registered-feature-flags'),

  addPreRegisteredTeamRelationship: (data: { supervisorEmail: string; subordinateEmail: string; accessMode: ClientAccessMode }) =>
    WaterlilyClient.post<PreRegisteredTeamRelationship>('/wizard-pre-registered-team-relationship', data),

  addPreRegisteredTeamRelationshipsBulk: (data: { relationships: Array<{ supervisorEmail: string; subordinateEmail: string; accessMode: ClientAccessMode }> }) =>
    WaterlilyClient.post<PreRegisteredTeamRelationshipsBulkResponse>('/wizard-pre-registered-team-relationships-bulk', data),

  searchPreRegisteredTeamRelationship: (data: { email: string }) =>
    WaterlilyClient.post<PreRegisteredTeamRelationshipSearchResponse>('/search/wizard-pre-registered-team-relationship', data),

  deletePreRegisteredTeamRelationship: (id: string) =>
    WaterlilyClient.delete<{ success: boolean }>(`/wizard-pre-registered-team-relationship/${id}`),

  fetchPreRegisteredTeamRelationships: () =>
    WaterlilyClient.get<PreRegisteredTeamRelationship[]>('/wizard-pre-registered-team-relationships'),

  probeCarescoutState: ({ quoteId, clientId, state }: CarescoutStateProbeParams) =>
    WaterlilyClient.post<CarescoutStateProbeResponse>(
      `/wizard/${quoteId}/traditional-ltc-illustration-probe`,
      { clientId, state },
    ),

  fetchSelfRecord: () => WaterlilyClient.get<SelfRecordResponse>('/wizard-self-record'),
};
